import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { HERO_YOUTUBE_ID, HERO_POSTER } from "../../data/site";
import { useBooking } from "./BookingModal";
import { useLang } from "../../i18n/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const [videoReady, setVideoReady] = useState(false);
  const { openBooking } = useBooking();
  const { t } = useLang();
  const playerRef = useRef(null);

  useEffect(() => {
    const init = () => {
      try {
        playerRef.current = new window.YT.Player("hero-yt-player", {
          videoId: HERO_YOUTUBE_ID,
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: 1,
            mute: 1,
            controls: 0,
            loop: 1,
            playlist: HERO_YOUTUBE_ID,
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
          },
          events: {
            onReady: (e) => {
              e.target.mute();
              e.target.playVideo();
            },
            onStateChange: (e) => {
              if (e.data === 1) setVideoReady(true);
            },
          },
        });
      } catch {
        /* poster stays visible */
      }
    };

    if (window.YT && window.YT.Player) {
      init();
    } else {
      if (!document.getElementById("yt-iframe-api")) {
        const tag = document.createElement("script");
        tag.id = "yt-iframe-api";
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === "function") prev();
        init();
      };
    }

    return () => {
      try {
        playerRef.current && playerRef.current.destroy();
      } catch {
        /* noop */
      }
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative h-screen w-full overflow-hidden"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src={HERO_POSTER}
          alt="Tribe Beach Lounge on Tigaki beach, Kos, Greece"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          data-testid="hero-video"
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full transition-opacity duration-1000 ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
        >
          <div id="hero-yt-player" className="w-full h-full" />
        </div>
      </div>

      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col justify-center">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="text-sm sm:text-base uppercase tracking-luxe text-white font-bold mb-6"
          style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
        >
          {t.hero.location}
        </motion.span>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45 }}
          className="hidden sm:block mb-7"
        >
          <button
            data-testid="hero-book-now"
            onClick={openBooking}
            className="inline-flex items-center bg-accent text-accent-foreground text-xs uppercase tracking-luxe px-8 py-4 hover:bg-white hover:text-foreground transition-colors duration-300 shadow-lg"
          >
            {t.common.book}
          </button>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.6 }}
          className="font-serif font-light text-white text-5xl sm:text-6xl lg:text-8xl leading-[1.02] max-w-4xl"
          style={{ textShadow: "0 4px 28px rgba(0,0,0,0.45)" }}
        >
          {t.hero.headline}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-7 max-w-md text-white text-base md:text-lg font-light"
          style={{ textShadow: "0 2px 16px rgba(0,0,0,0.5)" }}
        >
          {t.hero.sub}
        </motion.p>
      </div>

      <button
        onClick={() => scrollTo("about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/80 hover:text-white transition-colors"
        aria-label="Scroll down"
      >
        <ChevronDown size={28} className="animate-bounce" />
      </button>
    </section>
  );
};
